/**
 * W-8 卖家商品管理(我的发布)。视觉真源 design/seller-products.html;业务真源 App SellerProductsPage。
 * 4 组分状态渲染 + 行操作(编辑/下架/重新上架)+ 风控条 + 申诉 + 五态。
 * 进屏 KYC 门禁由 router 的 <KycGate> 包裹。下架二次确认走 C-5 对话框。
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  changeProductStatus,
  deleteProduct,
  listSellerProducts,
  type SellerProductListItem,
} from '@/api/products';
import { getRiskStatus } from '@/api/risk';
import { ServerError } from '@/api/client';
import { queryClient } from '@/lib/queryClient';
import { STATUS_LABEL } from '@/lib/productLabels';
import { toast } from '@/components/ui/toast';
import { Dialog } from '@/components/ui/Dialog';
import { groupByStatus } from './logic';
import { SellerProductRow } from './SellerProductRow';
import { RiskStatusBanner } from './RiskStatusBanner';
import { AppealDialog } from './AppealDialog';

const truncate = (s: string | null | undefined, n: number): string =>
  s ? [...s].slice(0, n).join('') : '';

export function SellerProductsPage() {
  const navigate = useNavigate();
  const [offlineTarget, setOfflineTarget] = useState<SellerProductListItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SellerProductListItem | null>(null);
  const [appealOpen, setAppealOpen] = useState(false);
  const [actionPending, setActionPending] = useState(false);

  const listQuery = useQuery({ queryKey: ['sellerProducts'], queryFn: listSellerProducts });
  const riskQuery = useQuery({ queryKey: ['risk-status'], queryFn: getRiskStatus });

  const onEdit = (id: string) => navigate(`/publish?mode=edit&productId=${id}`);

  const doChangeStatus = async (item: SellerProductListItem, target: 'OFFLINE' | 'ONLINE') => {
    if (actionPending) return;
    setActionPending(true);
    try {
      const resp = await changeProductStatus(item.id, target);
      if (target === 'OFFLINE') {
        toast.success('已下架');
      } else if (resp.status === 'ONLINE') {
        toast.success('已重新上架');
      } else if (resp.status === 'PENDING_AUDIT') {
        toast.info('已重新提交审核');
      } else if (resp.status === 'REJECTED') {
        toast.error(`审核未通过:${truncate(resp.rejectionReason, 50) || '内容不符合规范'}`);
      } else {
        // 意外状态兜底,避免静默刷新无反馈
        toast.info('已提交,请稍后查看状态');
      }
      void queryClient.invalidateQueries({ queryKey: ['sellerProducts'] });
    } catch (err) {
      // 操作失败不 invalidate,保留旧列表
      toast.error(err instanceof ServerError ? err.message : '操作失败,请稍后再试');
    } finally {
      setActionPending(false);
    }
  };

  const confirmOffline = async () => {
    const item = offlineTarget;
    setOfflineTarget(null);
    if (item) await doChangeStatus(item, 'OFFLINE');
  };

  const confirmDelete = async () => {
    const item = deleteTarget;
    setDeleteTarget(null);
    if (!item || actionPending) return;
    setActionPending(true);
    try {
      await deleteProduct(item.id);
      toast.success('已删除');
      void queryClient.invalidateQueries({ queryKey: ['sellerProducts'] });
    } catch (err) {
      toast.error(err instanceof ServerError ? err.message : '删除失败,请稍后再试');
    } finally {
      setActionPending(false);
    }
  };

  // ---- 五态 ----
  if (listQuery.isLoading) {
    return (
      <div className="inner section">
        <div className="state">
          <div className="skeleton" style={{ width: 48, height: 48, borderRadius: '50%' }} />
          <p className="muted sm mt-4">加载中…</p>
        </div>
      </div>
    );
  }

  if (listQuery.isError) {
    return (
      <div className="inner section">
        <div className="state">
          <div className="ic">⚠️</div>
          <h2 className="h2 mb-3">加载失败</h2>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => listQuery.refetch()}
          >
            重试
          </button>
        </div>
      </div>
    );
  }

  const groups = groupByStatus(listQuery.data?.items ?? []);

  if (groups.length === 0) {
    return (
      <div className="inner section">
        <div className="state">
          <div className="ic">📦</div>
          <h2 className="h2 mb-3">还没发布商品,去发一个吧</h2>
          <button type="button" className="btn btn-primary btn-lg" onClick={() => navigate('/publish')}>
            去发布
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="inner section">
      <div className="row between mb-8">
        <div>
          <h1 className="h1">我的发布</h1>
          <p className="muted mt-2">管理你已发布的商品:编辑、下架、重新上架。</p>
        </div>
        <button type="button" className="btn btn-primary btn-lg" onClick={() => navigate('/publish')}>
          ＋ 发布新商品
        </button>
      </div>

      <RiskStatusBanner risk={riskQuery.data} onAppeal={() => setAppealOpen(true)} />

      {groups.map((g) => (
        <section key={g.status} className="mb-8">
          <h2 className="h3 mb-4">
            {STATUS_LABEL[g.status]}（{g.items.length}）
          </h2>
          <div className="list">
            {g.items.map((item) => (
              <SellerProductRow
                key={item.id}
                item={item}
                onEdit={onEdit}
                onOffline={setOfflineTarget}
                onRepublish={(it) => doChangeStatus(it, 'ONLINE')}
                onDelete={setDeleteTarget}
              />
            ))}
          </div>
        </section>
      ))}

      {/* 下架二次确认(C-5) */}
      <Dialog open={!!offlineTarget} onClose={() => setOfflineTarget(null)} labelledBy="offline-title">
        <h3 id="offline-title">确认下架</h3>
        <p className="sm muted mb-6">下架后买家将无法看到此商品,确定下架?</p>
        <div className="row gap-4" style={{ justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setOfflineTarget(null)}
            disabled={actionPending}
          >
            取消
          </button>
          <button
            type="button"
            className="btn btn-destructive"
            onClick={confirmOffline}
            disabled={actionPending}
          >
            确定下架
          </button>
        </div>
      </Dialog>

      {/* 删除二次确认(软删除,不可从列表恢复) */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} labelledBy="delete-title">
        <h3 id="delete-title">确认删除</h3>
        <p className="sm muted mb-6">删除后该商品将从「我的发布」移除,不可恢复。确定删除?</p>
        <div className="row gap-4" style={{ justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setDeleteTarget(null)}
            disabled={actionPending}
          >
            取消
          </button>
          <button
            type="button"
            className="btn btn-destructive"
            onClick={confirmDelete}
            disabled={actionPending}
          >
            确定删除
          </button>
        </div>
      </Dialog>

      <AppealDialog open={appealOpen} onClose={() => setAppealOpen(false)} />
    </div>
  );
}
